import styled from '@emotion/styled';
import React from 'react';
import PlaneIcon from 'src/assets/images/plane.png';
import { getBrandLogo, i18n, mediaQueryMax, navigate, resolvePromo, showMessage, useGeneralSoContext, useLoader, useSelectOffer } from 'src/common';
import { isRateLimitDenial } from 'src/common/apiGraphql/captcha';
import { BrandCalculationDto, IncludedCoverageDto, PickedCalculationDto, TravelInsuranceRange, TravelInsuranceType } from 'src/common/apiGraphql/types/types';
import { Button, Flex, Tag, Text } from 'src/components';
import { AppRoutes } from 'src/pages/app/_router/app.routes';
import { colors } from 'src/theme';

interface OfferCardProps {
  offer: BrandCalculationDto;
  insuranceType?: TravelInsuranceType | null;
  insuranceRange?: TravelInsuranceRange | null;
}

const OfferCardContainer = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 16px;
`;

const HeaderBar = styled.div`
  background: ${colors.border8};
  padding: 12px 16px;
  text-align: center;
`;

const CompanySection = styled.div`
  padding: 20px 16px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CoverageItemContainer = styled.div`
  background: ${colors.background6};
  border-radius: 8px;
  padding: 16px;
  margin: 0 16px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CoverageContent = styled.div`
  flex: 1;
`;

const CoverageTitle = styled.div`
  font-weight: bold;
  color: ${colors.text1};
  margin-bottom: 4px;
  font-size: 14px;
`;

const CoverageDescription = styled.div`
  color: ${colors.text3};
  font-size: 12px;
  line-height: 1.4;
`;

const ButtonContainer = styled.div`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${colors.border6};
`;

const YourChoices = styled(Text)`
  margin-left: 16px;
  background-color: ${colors.green1};
  width: 180px;
  text-align: center;
  border-radius: 8px 8px 0 0;
  padding-top: 4px;
  padding-bottom: 4px;
`;

const FullPrice = styled(Text)`
  text-decoration: line-through;
  color: ${colors.text3};
`;

const PromoBlock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;

  ${mediaQueryMax('mobile')} {
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
  }
`;

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#10B981" />
  </svg>
);

const CrossIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="#EF4444" />
  </svg>
);

const BrandLogo = styled.img`
  height: 32px;
`;

const IconContainer = styled.div`
  width: 32px;
  height: 37px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const OfferCard: React.FC<OfferCardProps> = ({
  offer,
  insuranceType,
  insuranceRange
}) => {
  const [selectOffer] = useSelectOffer();
  const { soId } = useGeneralSoContext();
  const { startLoader, stopLoader } = useLoader();

  const promo = resolvePromo({
    brandCode: offer.brandCode,
    insuranceType,
    insuranceRange,
    totalPrice: offer.price.totalPrice,
  });

  // Prefer a real "before discount" price from the API; fall back to the
  // promo-config computed price (e.g. totalPrice * multiplier).
  const apiFullPrice =
    offer.price.price && offer.price.totalPrice && offer.price.price > offer.price.totalPrice
      ? offer.price.price
      : undefined;
  const strikethroughPrice = apiFullPrice ?? promo?.strikethroughPrice;

  const transformToPickedCalculation = (offer: BrandCalculationDto): PickedCalculationDto => {
    return {
      brandCode: offer.brandCode,
      price: offer.price.totalPrice,
      product: {
        _id: offer.product._id,
        name: offer.product.name,
        brandId: {
          _id: offer.product.brandId._id,
          code: offer.product.brandId.code,
          name: offer.product.brandId.name,
          shortName: offer.product.brandId.shortName,
        },
        productTypeSpecific: {
          onlyPackage: offer.product.productTypeSpecific.onlyPackage,
          packageName: offer.product.productTypeSpecific.packageName,
        },
      },
      includedCoverages: offer.includedCoverages.map(coverage => ({
        code: coverage.code,
        variant: coverage.variant,
        label: coverage.label,
        description: coverage.description,
        variantInfo: coverage.variantInfo,
        disabled: coverage.disabled
      })),
    };
  };

  const handleSelectOffer = () => {
    startLoader();
    if (!soId) return;

    const pickedCalculation = transformToPickedCalculation(offer);

    selectOffer({
      pickedCalculation,
      soId: soId
    }).then(() => {
      navigate(`${AppRoutes.WEBSHOP.STEP_4}`);
    }).catch((error) => {
      // RATE_LIMITED / IP_BLOCKED are already toasted by the Apollo error link;
      // a generic toast here would replace that message.
      if (isRateLimitDenial(error)) return;
      showMessage({
        type: 'error',
        message: i18n.t('common.messages.error'),
        duration: 5000,
        fixed: true
      });
    }).finally(() => {
      stopLoader();
    });
  };

  const renderCoverage = (includedCoverage: IncludedCoverageDto, index: number) => {
    const icon = includedCoverage.code === "travelHealthInsurance" ? <img src={PlaneIcon} alt="" /> : <CheckIcon />;
    return (
      <CoverageItemContainer key={index} style={index === 0 ? { borderRadius: '0 8px 8px 8px' } : {}}>
        <IconContainer>
          {!includedCoverage.disabled && icon}
          {includedCoverage.disabled && <CrossIcon />}
        </IconContainer>
        <CoverageContent>
          <CoverageTitle>{includedCoverage.label}</CoverageTitle>
          <CoverageDescription>{includedCoverage.variantInfo}</CoverageDescription>
        </CoverageContent>
      </CoverageItemContainer>
    );
  };


  return (
    <OfferCardContainer>
      <HeaderBar>
        <Text type="tiny" color={colors.text2} bold>
          {!offer.product.productTypeSpecific.onlyPackage ? i18n.t("components.cards.offerCard.tailored") : i18n.t("components.cards.offerCard.package")}
        </Text>
      </HeaderBar>

      <CompanySection>
        <BrandLogo src={getBrandLogo(offer.brandCode)} alt={offer.product.brandId.shortName || offer.brandCode} />
        <Flex gap={16} align="center">
          <PromoBlock>
            {promo?.badgeTextKey && (
              <Tag background={colors.green1} value={i18n.t(promo.badgeTextKey)} bold />
            )}
            {strikethroughPrice != null && (
              <FullPrice type="medium" color={colors.text3} bold>
                {strikethroughPrice.toFixed(2)}€
              </FullPrice>
            )}
          </PromoBlock>
          <Text type="large" color={colors.text1} bold>
            {offer.price.totalPrice ? offer.price.totalPrice.toFixed(2) : ''}€
          </Text>
        </Flex>
      </CompanySection>

      <YourChoices type="small" color={colors.text2} bold>
        {offer.product.productTypeSpecific.packageName || i18n.t("components.cards.offerCard.yourChoices")}
      </YourChoices>
      {offer.includedCoverages.map((coverage, index) => renderCoverage(coverage, index))}

      <ButtonContainer>
        <div></div>
        <Button onClick={handleSelectOffer} type="button" btnType="primary" size="large" style={{ width: 180 }}>
          {i18n.t("components.cards.offerCard.buy")}
        </Button>
      </ButtonContainer>
    </OfferCardContainer>
  );
};
